import React from "react"
import {useSafeIntl} from "./intlHook"
import type {IntlCtx} from "./types"

const fallbackIntl = {
  formatMessage: (msg: {id?: string; defaultMessage?: string}) => msg.defaultMessage ?? msg.id ?? ""
} as unknown as IntlCtx

/**
 * Context exposing Intl imperative apis for displaying strings and numbers such as `formatMessage`
 *
 * NOTE: please prefer using `Formatted*` components exposed by `@irati/utils/lang` wherever possible
 *
 * @see [format.js imperative api](https://formatjs.github.io/docs/react-intl/api)
 */
export interface InjectIntl {
  formatMessage: IntlCtx["formatMessage"] // this is just exposed for convenience as the most common api
  intl: IntlCtx
}

export interface InjectIntlProps extends InjectIntl {
  forwardedRef?: any
}

/**
 * HOC exposing `intl` to a class components props
 *
 * @deprecated use `useIntl`
 */
export function injectIntl<Props>(Comp: React.ComponentType<Props & InjectIntlProps>, {withRef = false} = {}) {
  if (withRef) {
    return function ReffedInjectedIntlWrapper({ref, ...props}: Props & {ref?: React.Ref<any>}) {
      // https://overreacted.io/how-does-react-tell-a-class-from-a-function/
      if (!Comp.prototype?.isReactComponent) {
        throw new Error("injectIntl shouldn't be used with function components, please use `useIntl` hook instead")
      }
      const intl = useSafeIntl()
      const resolvedIntl = intl ?? fallbackIntl
      return <Comp {...props} intl={resolvedIntl} formatMessage={resolvedIntl.formatMessage} ref={ref} />
    }
  }

  return function InjectedIntlWrapper(props: Props) {
    const intl = useSafeIntl()
    const resolvedIntl = intl ?? fallbackIntl
    return <Comp {...props} intl={resolvedIntl} formatMessage={resolvedIntl.formatMessage} />
  }
}
